import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaEnvelope, FaPhoneAlt } from 'react-icons/fa';
import { GrContact } from "react-icons/gr";
import { dataCy, SELECTORS } from '../utils/dataCy';

const Contact = () => {
  const { t } = useTranslation('home'); // Namespace 'contact' para traducciones
  const hostname = window.location.hostname;
  const isExcentCapital = hostname === 'excent.capital' || hostname.endsWith('.excent.capital');
  const supportEmail = isExcentCapital ? 'support@excent.capital' : 'support@excentcapital.com';

  return (
    <div className="pt-[7rem] sm:pt[7rem] md:pt-[9rem] lg:pt-[9rem] xl:pt-[9rem] 2xl:pt-[9rem] px-4 sm:px-8 py-10 sm:py-16 bg-gray-50 dark:bg-primaryBg text-gray-800 dark:text-gray-200">
      <div className="relative max-w-4xl mx-auto text-center">
        {/* Título principal */}
        <motion.h2
          className="text-4xl sm:text-5xl font-extrabold mb-6 text-gray-800 dark:text-gray-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {t('contact.contactTitle', 'We Are Delighted To Help And Support Our Clients')}
        </motion.h2>

        {/* Descripción */}
        <motion.p
          className="text-base sm:text-lg mb-12 text-gray-600 dark:text-gray-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {t(
            'contact.contactDescription',
            'Our team is just an email away and ready to answer all of your questions, so let us know how we can help you today.'
          )}
        </motion.p>

        {/* Cards de contacto */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Card 1: Email */}
          <motion.div
            className="p-6 bg-white dark:bg-grayButtonSolid rounded-lg shadow-md hover:shadow-xl transition-shadow hover:scale-105 flex flex-col justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <FaEnvelope className="text-secondBg text-3xl mb-4" />
            <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
              {t('contact.writeToUs', 'Write to us')}
            </h3>
            <p className="text-secondBg">
              <a href={`mailto:${supportEmail}`} className="hover:underline" data-cy={dataCy(SELECTORS.LINK, 'contact-email')}>
                {supportEmail}
              </a>
            </p>
          </motion.div>

          {/* Card 2: Support line (Seychelles) - Solo visible en excent.capital */}
          {isExcentCapital && (
            <motion.div
              className="p-6 bg-white dark:bg-grayButtonSolid rounded-lg shadow-md hover:shadow-xl transition-shadow hover:scale-105 flex flex-col justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <FaPhoneAlt className="text-secondBg text-3xl mb-4" />
              <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                {t('contact.supportSeychelles', 'Support line (Seychelles)')}
              </h3>
              <p className="text-secondBg">
                <a href="tel:+2484373651" className="hover:underline" data-cy={dataCy(SELECTORS.LINK, 'contact-phone-seychelles')}>
                  +248 437 3651
                </a>
              </p>
            </motion.div>
          )}

          {/* Card 3: Support line (International) */}
          {/* <motion.div
            className="p-6 bg-white dark:bg-grayButtonSolid rounded-lg shadow-md hover:shadow-xl transition-shadow hover:scale-105 flex flex-col justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <FaPhoneAlt className="text-secondBg text-3xl mb-4" />
            <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
              {t('contact.supportInternational', 'Support line (International)')}
            </h3>
            <p className="text-secondBg">
              <a href="tel:+442038403680" className="hover:underline" data-cy={dataCy(SELECTORS.LINK, 'contact-phone-international')}>
                +44 2038 403 680
              </a>
            </p>
          </motion.div> */}
        </div>

        {/* Botón adicional */}
        <motion.div
          className="mt-12 flex justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <a
            href={`mailto:${supportEmail}`}
            className="px-8 py-3 bg-secondBg text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors flex items-center gap-2 w-fit"
            data-cy={dataCy(SELECTORS.LINK, 'contact-getintouch')}
          >
            <GrContact className="text-xl" />
            {t('contact.getInTouch', 'Get in Touch')}
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
